// src/pages/clients/Traffic.js - Componente mejorado con reset del cliente seleccionado
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, LinearProgress,
    Tabs, Tab, Alert, Autocomplete, TextField, CircularProgress
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    NetworkCheck as NetworkIcon,
    SignalCellularAlt as SignalIcon,
    DateRange as WeekIcon,
    InsertChart as MonthIcon,
    Search as SearchIcon
} from '@mui/icons-material';

// Importar componentes de tráfico
import RealtimeTraffic from '../../components/traffic/RealtimeTraffic';
import DailyTraffic from '../../components/traffic/DailyTraffic';
import MonthlyTraffic from '../../components/traffic/MonthlyTraffic';
import { get } from '../../services/apiService';

// Función para obtener un nombre legible del cliente
const getClientDisplayName = (client) => {
    if (!client) return '';

    // Comprobar diferentes formatos posibles (camelCase o snake_case)
    const firstName = client.first_name || client.firstName || '';
    const lastName = client.last_name || client.lastName || '';
    const queueName = client.queue_name || client.queueName || '';
    const ipAddress = client.ip_address || client.ipAddress || '';

    // Si hay nombres, usarlos
    if (firstName || lastName) {
        return `${firstName} ${lastName} (${ipAddress || queueName || 'Sin ID'})`.trim();
    }

    // Si hay un nombre completo ya formateado
    if (client.fullName) {
        return `${client.fullName} (${ipAddress || queueName || 'Sin ID'})`.trim();
    }

    // Si no hay nombres, usar queue_name o IP
    return queueName || ipAddress || 'Cliente sin datos';
};

// Componente principal para la página de tráfico
function Traffic() {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [isListView, setIsListView] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [filters, setFilters] = useState({
        days: 7,
        months: 12
    });
    const [previousPath, setPreviousPath] = useState('');

    // Referencias y hooks
    const navigate = useNavigate();
    const location = useLocation();

    // Monitorear cambios en la ruta para saber si estamos viniendo desde el sidebar
    useEffect(() => {
        if (previousPath !== '' && previousPath !== location.pathname && location.pathname === '/clients/traffic' && !location.search) {
            // Si venimos de otra ruta (no es la carga inicial) y llegamos a la ruta de tráfico sin parámetros,
            // resetear el cliente seleccionado
            setSelectedClient('');
            setIsListView(true);
            setActiveTab(0); // Volver a la pestaña de tiempo real
            setRefreshKey(prev => prev + 1);
        }

        setPreviousPath(location.pathname + location.search);
    }, [location.pathname, location.search, previousPath]);

    // Procesar parámetros de URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const clientIdFromUrl = params.get('client');

        if (clientIdFromUrl) {
            setSelectedClient(clientIdFromUrl);
            setIsListView(false);
            setRefreshKey(prev => prev + 1);
        } else if (location.pathname === '/clients/traffic' && !location.search) {
            // Solo resetear si estamos en la ruta base sin parámetros
            setIsListView(true);
        }
    }, [location.pathname, location.search]);

    // Normalizar datos del cliente para manejar diferentes formatos de la API
    const normalizeClientData = (clientData) => {
        // Asegurarse de que el cliente tenga un formato consistente
        return {
            id: clientData.id,
            // Manejar diferentes formatos de campo: snake_case o camelCase
            first_name: clientData.first_name || clientData.firstName || '',
            last_name: clientData.last_name || clientData.lastName || '',
            queue_name: clientData.queue_name || clientData.queueName || '',
            ip_address: clientData.ip_address || clientData.ipAddress || '',
            status: clientData.status || 'unknown',
            // Guardar datos originales para depuración
            originalData: clientData
        };
    };

    // Cargar lista de clientes
    const fetchClients = useCallback(async () => {
        try {
            setLoading(true);
            console.log('Cargando lista de clientes...');

            // Agregar un parámetro único para evitar caché del navegador
            const timestamp = new Date().getTime();
            const endpoint = `/api/clients?active=true&_nocache=${timestamp}`;

            const data = await get(endpoint, false, 30000, false);

            if (Array.isArray(data) && data.length > 0) {
                console.log(`Cargados ${data.length} clientes con éxito`);

                // Procesamiento de datos para normalizar formato
                const processedClients = data.map(client => normalizeClientData(client));
                setClients(processedClients);
            } else {
                console.warn('La API devolvió un formato no esperado:', data);
                setClients([]);
                setError('No se encontraron clientes activos o el formato de respuesta es incorrecto.');
            }
        } catch (error) {
            console.error('Error al cargar clientes:', error);
            setError(`Error al cargar clientes: ${error.message}. Por favor, inténtelo de nuevo.`);
            setClients([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Cargar lista de clientes al montar
    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    // Cambiar de pestaña
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Manejar selección de cliente
    const handleClientChange = (event, newValue) => {
        // Aquí newValue será el objeto cliente completo seleccionado del Autocomplete
        const clientId = newValue?.id || '';
        console.log('Cliente seleccionado:', clientId, newValue);

        if (clientId) {
            navigate(`/clients/traffic?client=${clientId}`, { replace: true });
            setSelectedClient(clientId);
            setIsListView(false);
            setRefreshKey(prev => prev + 1);
        } else {
            navigate('/clients/traffic', { replace: true });
            setSelectedClient('');
            setIsListView(true);
        }
    };

    // Volver a la vista de lista
    const handleResetSelection = () => {
        console.log('Volviendo a vista de lista');
        navigate('/clients/traffic', { replace: true });
        setSelectedClient('');
        setIsListView(true);
        setRefreshKey(prev => prev + 1);
    };

    // Ver detalles de un cliente
    const handleViewDetails = (clientId) => {
        console.log('Ver detalles del cliente:', clientId);
        const client = clients.find(c => c.id === clientId);
        handleClientChange(null, client);
    };

    // Refrescar datos
    const handleRefresh = () => {
        console.log('Refrescando datos...');
        if (isListView) {
            fetchClients();
        } else {
            setRefreshKey(prev => prev + 1);
        }
    };

    // Encontrar el cliente seleccionado
    const selectedClientObject = clients.find(c => c.id === selectedClient) || null;

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            {/* Cabecera con título y controles */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', md: 'center' },
                    gap: 2,
                    backgroundColor: 'rgb(246, 248, 250)'
                }}
            >
                <Box display="flex" alignItems="center">
                    <NetworkIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h5" component="h1" fontWeight="500">
                        Monitoreo de Tráfico
                    </Typography>
                </Box>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center', 
                    gap: 2, 
                    width: { xs: '100%', md: '65%' } 
                }}>
                    <Autocomplete
                        id="client-selector"
                        options={clients}
                        getOptionLabel={(option) => getClientDisplayName(option)}
                        value={selectedClientObject}
                        onChange={handleClientChange}
                        fullWidth
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Cliente"
                                placeholder="Buscar cliente..."
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <SearchIcon color="action" sx={{ mr: 1 }} />
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2" fontWeight="bold">
                                        {option.first_name
                                            ? `${option.first_name} ${option.last_name}`
                                            : (option.fullName || option.queue_name)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.ip_address ? `IP: ${option.ip_address}` : ''}
                                        {option.queue_name ? ` | Queue: ${option.queue_name}` : ''}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        loading={loading}
                        loadingText="Cargando clientes..."
                        noOptionsText="No se encontraron clientes"
                        clearOnBlur={false}
                        disableClearable={false}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        filterOptions={(options, state) => {
                            const inputValue = state.inputValue.toLowerCase().trim();

                            if (inputValue === '') {
                                return options;
                            }

                            return options.filter(client => {
                                const firstName = client.first_name || client.firstName || '';
                                const lastName = client.last_name || client.lastName || '';
                                const queueName = client.queue_name || client.queueName || '';
                                const ipAddress = client.ip_address || client.ipAddress || '';
                                const fullName = client.fullName || `${firstName} ${lastName}`;

                                return firstName.toLowerCase().includes(inputValue) ||
                                    lastName.toLowerCase().includes(inputValue) ||
                                    queueName.toLowerCase().includes(inputValue) ||
                                    ipAddress.includes(inputValue) ||
                                    fullName.toLowerCase().includes(inputValue);
                            });
                        }}
                    />

                    {!isListView && (
                        <Button
                            variant="outlined"
                            onClick={handleResetSelection}
                        >
                            VER TODOS
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={loading}
                    >
                        ACTUALIZAR
                    </Button>
                </Box>
            </Paper>

            {/* Errores */}
            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Tabs de navegación */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                        width: '100%',
                        '& .MuiTab-root': {
                            minWidth: { xs: 'auto', sm: 160 },
                            flex: { xs: 1, sm: 'none' }
                        }
                    }}
                >
                    <Tab
                        icon={<SignalIcon />}
                        label="TIEMPO REAL"
                    />
                    <Tab
                        icon={<WeekIcon />}
                        label="ÚLTIMOS 7 DÍAS"
                    />
                    <Tab
                        icon={<MonthIcon />}
                        label="HISTÓRICO ANUAL"
                    />
                </Tabs>
            </Paper>

            {/* Indicador de carga */}
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Contenido de las pestañas */}
            <Box sx={{ mt: 2 }}>
                {/* Tab 0: Tráfico en tiempo real */}
                {activeTab === 0 && (
                    <RealtimeTraffic
                        key={`realtime-${selectedClient || 'all'}-${refreshKey}`}
                        clientId={selectedClient}
                        setError={setError}
                        onViewDetails={handleViewDetails}
                        isListView={isListView}
                    />
                )}

                {/* Tab 1: Tráfico diario */}
                {activeTab === 1 && (
                    <DailyTraffic
                        key={`daily-${selectedClient || 'all'}-${filters.days}-${refreshKey}`}
                        clientId={selectedClient}
                        days={filters.days}
                        setError={setError}
                    />
                )}

                {/* Tab 2: Tráfico mensual */}
                {activeTab === 2 && (
                    <MonthlyTraffic
                        key={`monthly-${selectedClient || 'all'}-${filters.months}-${refreshKey}`}
                        clientId={selectedClient}
                        months={filters.months}
                        setError={setError}
                    />
                )}
            </Box>
        </Box>
    );
}

export default Traffic;